// Density overlay — H3 hex aggregation. Bins all geo-bearing signals into
// H3 cells at a zoom-derived resolution, then renders each cell as a
// PolygonLayer feature colored by `count × max-relevance`.
//
// We use deck.gl's PolygonLayer with h3-js cellToBoundary instead of
// pulling in `@deck.gl/geo-layers` for one type — keeps the SDK lean.

import { PolygonLayer } from '@deck.gl/layers';
import type { intel as i } from '@eidollon/contracts';
import { cellToBoundary, latLngToCell } from 'h3-js';
import { type RGBA, getMapPalette } from '../colors.js';
import { h3ResForZoom, withGeom } from './_shared.js';
import type { LayerSpec } from './registry.js';

interface HexBin {
  cell: string;
  count: number;
  maxSeverity: number;
  /** [ring0]: array of [lng, lat] in GeoJSON order. */
  polygon: [number, number][];
}

function aggregate(rows: readonly i.IntelSignal[], res: number): HexBin[] {
  const bins = new Map<string, { count: number; maxSev: number }>();
  for (const r of withGeom(rows)) {
    // biome-ignore lint/style/noNonNullAssertion: withGeom() guarantees geom.
    const cell = latLngToCell(r.geom!.lat, r.geom!.lng, res);
    const cur = bins.get(cell);
    if (cur) {
      cur.count += 1;
      if (r.severity > cur.maxSev) cur.maxSev = r.severity;
    } else {
      bins.set(cell, { count: 1, maxSev: r.severity });
    }
  }
  const out: HexBin[] = [];
  for (const [cell, agg] of bins) {
    const ring = cellToBoundary(cell, true) as [number, number][]; // GeoJSON [lng,lat]
    out.push({ cell, count: agg.count, maxSeverity: agg.maxSev, polygon: ring });
  }
  return out;
}

export const densityLayer: LayerSpec = {
  id: 'density',
  label: 'DEN · density',
  kind: 'hex',
  signalTypes: [],
  color: 'var(--primary)',
  colorAxis: 'severity',
  description: 'H3 hex aggregation tinted by max-severity per cell',
  classification: 'OPEN',
  legend: {
    swatch: 'var(--primary)',
    label: 'DEN',
    hint: 'H3 hex aggregation (count × relevance)',
  },
  select: (rows) => withGeom(rows.slice()),
  visibleAtZoom: (z) => z >= 5 && z <= 8,
  deckLayer: ({ rows, viewport }) => {
    const res = h3ResForZoom(viewport.zoom);
    const bins = aggregate(rows, res);
    const maxCount = bins.reduce((m, b) => Math.max(m, b.count), 1);
    // Capture the palette snapshot once at builder-call time. The per-row
    // callback below reads from this closure — never from getMapPalette() —
    // so per-frame, per-cell color picks are pure closure lookups with no
    // function-call or property-access into module state.
    const palette = getMapPalette();
    const ramp = (score: number): RGBA => {
      // Heat ramp boundaries — mirrors severityRamp() in colors.ts. Inlined
      // here (not a function call) because deck.gl invokes getFillColor
      // per-cell per-frame and we want closure-only lookups in the hot path.
      if (score >= 90) return palette.sevHot;
      if (score >= 70) return palette.sevElevated;
      if (score >= 40) return palette.sevWatch;
      return palette.sevOk;
    };
    return new PolygonLayer<HexBin>({
      id: `intel-density-r${res}`,
      data: bins,
      pickable: false,
      stroked: true,
      filled: true,
      getPolygon: (b) => b.polygon,
      getFillColor: (b) => {
        // Hue = severity bucket (read from the closure-captured snapshot).
        // Alpha = composite of cell density and bucket weight, mapped into
        // [24, 160] so even sparse cells stay visible above the basemap.
        const [r, g, bl] = ramp(b.maxSeverity);
        const c = b.count / maxCount;
        const sev = b.maxSeverity / 100;
        const a = Math.round(24 + Math.min(1, c * 0.7 + sev * 0.5) * 136);
        return [r, g, bl, a];
      },
      getLineColor: palette.densityStroke,
      getLineWidth: 1,
      lineWidthUnits: 'pixels',
      updateTriggers: {
        getFillColor: viewport.zoom,
      },
    });
  },
};
